"use client";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import Navigation from "@/HomeFeatures/Navigation";
import BlogCard from "@/components/BlogCard";
import BlogHero from "./sections/BlogHero";
import TOCSidebar from "./sections/TOCSidebar";
import SubscribeCard from "./sections/SubscribeCard";
import AuthorCard from "./sections/AuthorCard";
import BlogBreadcrumb from "./sections/BlogBreadcrumb";
import { BlogDetailsProps } from "@/lib/Types";
import "./style.css";
import Image from "next/image";
import Images from "@/app/assets/assets";
import type { StaticImageData } from "next/image";
import DOMPurify from "isomorphic-dompurify";
import { blogFormatDate } from "@/lib/Utility";

const ContactSection = dynamic(() => import("@/HomeFeatures/Contact2"));
const Faq = dynamic(() => import("@/HomeFeatures/Faq"));
type ImageSource = string | StaticImageData;
export default function BlogDetails({
  initialBlog,
  initialFaq,
  initialRelated,
}: BlogDetailsProps) {
  const [mounted, setMounted] = useState(true);
  const [formattedDate, setFormattedDate] = useState({
    date: "",
    publishedDate: "",
  });

  const getImageSrc = (img: ImageSource | undefined): string => {
    if (!img) return "";
    return typeof img === "string" ? img : img.src;
  };

  const blogImageSrc = getImageSrc(initialBlog?.image);
  const authorImageSrc = getImageSrc(initialBlog?.authorImage);

  const getFormatId = (title: string) => {
    if (!title) return "";
    const cleanStr = title.replace(/<[^>]*>?/gm, "");
    return cleanStr.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  };

  useEffect(() => {
    setMounted(true);

    if (!initialBlog?.date) {
      setFormattedDate({
        date: "",
        publishedDate: "",
      });
      return;
    }

    try {
      const date = blogFormatDate(initialBlog.date);
      console.log(date, "date", initialBlog.date, "initialBlog.date")
      const publishedDate = blogFormatDate(initialBlog.publishedDate);
      console.log(publishedDate, "publishedDate", initialBlog.publishedDate, "initialBlog.publishedDate")

      if (date && !isNaN(date.getTime())) {
        setFormattedDate({
          date: date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          publishedDate:
            publishedDate && !isNaN(publishedDate.getTime())
              ? publishedDate.toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
              : "",
        });
      } else {
        setFormattedDate({
          date: initialBlog.date || "",
          publishedDate: initialBlog.publishedDate || "",
        });
      }
    } catch {
      setFormattedDate({
        date: initialBlog.date || "",
        publishedDate: initialBlog.publishedDate || "",
      });
    }
  }, [initialBlog?.date, initialBlog?.publishedDate]);

  return (
    <>
      <section className="relative h-[max-content] flex flex-col max-w-[100vw]">
        <div className="relative z-10 w-full">
          <div className="w-full h-full min-h-[100vh] bg-[#fff] flex flex-col pb-[50px] px-3 lg:px-0">
            <Navigation dark={false} />
            <BlogBreadcrumb selectedBlogName={initialBlog?.title} />

            <div className="w-[calc(100%_-_5%)] md:w-[calc(100%_-_10%)] max-w-[2500px] mx-auto mt-4 md:mt-6 lg:mt-10">
              <div className="flex flex-col lg:flex-row gap-[20px] md:gap-[30px] justify-between">
                {initialBlog ? (
                  <div className="w-full lg:flex-1 [@media(min-width:1600px)]:max-w-[70%] konstant-blog-content">
                    <BlogHero
                      title={initialBlog.title}
                      category={initialBlog.categoryName || "Web Development"}
                      date={mounted ? formattedDate : ""}
                      imageSrc={blogImageSrc}
                    />

                    {/* Quote Banner */}
                    {initialBlog.blogQoute && (
                      <div className="mt-[30px] mb-[30px] md:mt-[50px]">
                        <div className="bg-[#ecf0f6] bg-no-repeat bg-left-top min-h-[100px] md:h-[144px] relative overflow-hidden flex items-center justify-center px-3 md:px-5 py-4 md:py-0 text-center">
                          <Image
                            src={Images?.blogDetails.quote}
                            alt="Quote decoration"
                            className="absolute top-0 left-0 h-full w-auto !m-0 object-cover"
                            fill
                            sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
                            priority
                            fetchPriority="high"
                          />
                          <h6 className="relative z-10 max-w-[931px] font-medium italic text-[#00040b] text-[18px] md:text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] leading-[1.6]">
                            {initialBlog.blogQoute}
                          </h6>
                        </div>
                      </div>
                    )}

                    {/* Main Content */}
                    {mounted ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(initialBlog.content ?? ""),
                        }}
                      />
                    ) : (
                      <div aria-hidden className="min-h-[100px]" />
                    )}

                    {/* Table of Contents Sections */}
                    {mounted &&
                      initialBlog.tableOfContent?.map((item, index) => (
                        <section
                          id={item.title ? getFormatId(item.title) : `blog_desc${index + 1}`}
                          className="my-5 scroll-mt-24"
                          key={`${initialBlog.slug}-toc-${index}`}
                        >
                          {item.title && (
                            <h2
                              className="text-[23px] lg:text-[36px] font-[500] clash-font mb-[30px]! lg:mb-4"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(item.title),
                              }}
                            />
                          )}
                          {item.blog_description && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  String(item?.blog_description ?? ""),
                                ),
                              }}
                            />
                          )}
                        </section>
                      ))}
                  </div>
                ) : (
                  <p className="text-center py-20 text-gray-500">
                    No Blog Post Available
                  </p>
                )}

                {/* Sidebar */}
                <aside className="w-full lg:w-[350px] xl:w-[552px]">
                  <div className="sticky top-4 flex flex-col gap-4">
                    <div className="z-20">
                      <TOCSidebar
                        items={initialBlog?.tableOfContent ?? []}
                        faq={initialFaq}
                      />
                    </div>
                    <div className="hidden [@media(min-width:1600px)]:block">
                      <SubscribeCard />
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </div>
      </section>

      {initialFaq && initialFaq.length > 0 && (
        <Faq FaqArray={initialFaq} allopen={true} />
      )}

      <section className="mb-[50px] md:mb-[100px]">
        <div className="w-[calc(100%_-_5%)] md:w-[calc(100%_-_10%)] max-w-[2500px] mx-auto">
          <AuthorCard
            authorName={initialBlog?.author}
            authorImage={authorImageSrc || ""}
            authorDesc={initialBlog?.authorDesc}
          />
        </div>
      </section>

      {/* Related Blogs */}
      {Array.isArray(initialRelated) && initialRelated.length > 0 && (
        <section className="recent-blogs pb-[80px] md:pb-[150px]">
          <div className="w-[calc(100%_-_5%)] md:w-[calc(100%_-_10%)] max-w-[2500px] mx-auto">
            <h2 className="text-[36px] font-medium clash-font mb-10">
              Related Posts
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-[20px] md:gap-[30px]">
              {initialRelated?.map((blog) => {
                const imgSrc = getImageSrc(blog.image);
                return (
                  <BlogCard
                    key={blog.id ?? blog.slug}
                    imageSrc={imgSrc}
                    category={initialBlog.categoryName ?? "Uncategorized"}
                    title={blog.title}
                    description={blog.excerpt}
                    date={blog.modified}
                    href={`/blog/${blog.slug}`}
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      <ContactSection />
    </>
  );
}
